import type {
    IRegDataComparisonFieldCard,
    IRegDataComparisonMatchRow,
    IRegDataStagedChange,
    IRegSystemOption,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { buildFieldComparatorConfig, type FieldComparatorConfig } from "./reconciliationComparators";
import { computeReconciliationCardSummary } from "./reconciliationCardSummary";
import {
    GeographyLifecycleMessages,
    getStagedChangeKey,
    RegDataChangeTypes,
} from "./regDataAlignmentDisplay";
import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import type {
    ReconciliationActionIcon,
    ReconciliationGridCardModel,
    ReconciliationGridRow,
    ReconciliationRowState,
    SystemSideData,
    TemplateSideData,
} from "./reconciliationGridTypes";
import { ReconciliationActionIcons, ReconciliationRowStates } from "./reconciliationGridTypes";

function preferSystemOption(existing: IRegSystemOption, candidate: IRegSystemOption): IRegSystemOption {
    const existingScore = systemOptionPriority(existing);
    const candidateScore = systemOptionPriority(candidate);
    return candidateScore > existingScore ? candidate : existing;
}

function systemOptionPriority(option: IRegSystemOption): number {
    if (option.isActive && !option.isDeleted) return 3;
    if (!option.isDeleted) return 2;
    return 1;
}

function getParsedString(parsed: Record<string, unknown>, key: string): string {
    const value = parsed[key];
    if (value == null) return "";
    return String(value).trim();
}

/** Mirrors backend facility-operation join: coded rows on code, code-less rows on normalised description. */
function resolveFacilityOperationJoinKey(
    templateOption: { normalizedKey: string; rawValue: string; parsed?: Record<string, unknown> },
    systemOption?: IRegSystemOption
): string {
    if (systemOption) {
        return systemOption.normalizedKey;
    }

    const parsed = templateOption.parsed ?? {};
    const code = getParsedString(parsed, "code");
    if (code) {
        return templateOption.normalizedKey;
    }

    const description = getParsedString(parsed, "description") || templateOption.rawValue.trim();
    if (!description) {
        return templateOption.normalizedKey;
    }

    return description.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function resolveJoinKey(
    fieldKey: string,
    templateOption: { normalizedKey: string; rawValue: string; parsed?: Record<string, unknown> } | undefined,
    systemOption: IRegSystemOption | undefined
): string {
    if (fieldKey === WasteReturnRegFieldKeys.FacilityOperation) {
        if (templateOption) {
            return resolveFacilityOperationJoinKey(templateOption, systemOption);
        }
        if (systemOption) {
            return systemOption.normalizedKey;
        }
    }

    return templateOption?.normalizedKey ?? systemOption?.normalizedKey ?? "";
}

function buildSystemByKey(systemOptions: IRegSystemOption[], fieldKey: string): Map<string, IRegSystemOption> {
    const map = new Map<string, IRegSystemOption>();
    for (const option of systemOptions) {
        const key = resolveJoinKey(fieldKey, undefined, option);
        if (!key) continue;
        const existing = map.get(key);
        map.set(key, existing ? preferSystemOption(existing, option) : option);
    }
    return map;
}

function buildTemplateByKey(
    matches: IRegDataComparisonMatchRow[],
    fieldKey: string
): Map<string, IRegDataComparisonMatchRow> {
    const map = new Map<string, IRegDataComparisonMatchRow>();
    for (const match of matches) {
        const key = resolveJoinKey(fieldKey, match.templateOption, undefined);
        if (!key || map.has(key)) continue;
        map.set(key, match);
    }
    return map;
}

export function getStagedChangeForGridRow(
    fieldKey: string,
    normalizedKey: string,
    templateRawValue: string | null,
    systemRecordId: string | null,
    stagedChanges: IRegDataStagedChange[]
): IRegDataStagedChange | undefined {
    return stagedChanges.find((change) => {
        if (change.fieldKey !== fieldKey) return false;

        if (change.changeType === RegDataChangeTypes.SoftDeleteExisting) {
            return !!systemRecordId && change.systemRecordId === systemRecordId;
        }

        if (change.templateRawValue && templateRawValue) {
            return change.templateRawValue === templateRawValue;
        }

        if (change.systemRecordId && systemRecordId) {
            return change.systemRecordId === systemRecordId;
        }

        return false;
    });
}

function resolveRowState(
    hasTemplate: boolean,
    systemOption: IRegSystemOption | null,
    templateParsed: Record<string, unknown>,
    comparator: FieldComparatorConfig
): ReconciliationRowState {
    if (hasTemplate && !systemOption) {
        return ReconciliationRowStates.TemplateOnly;
    }

    if (!hasTemplate && systemOption) {
        if (systemOption.isDeleted || !systemOption.isActive) {
            return ReconciliationRowStates.SystemInactiveOrDeletedOnly;
        }
        return ReconciliationRowStates.SystemOnly;
    }

    if (hasTemplate && systemOption) {
        if (systemOption.isDeleted) {
            return ReconciliationRowStates.DeletedMatch;
        }
        if (!systemOption.isActive) {
            return ReconciliationRowStates.InactiveMatch;
        }
        if (comparator.hasFlags && !comparator.flagsAgree(templateParsed, systemOption)) {
            return ReconciliationRowStates.FlagDiff;
        }
        return ReconciliationRowStates.Match;
    }

    return ReconciliationRowStates.NoAction;
}

function resolveStageChangeType(rowState: ReconciliationRowState, fieldKey: string, comparator: FieldComparatorConfig): string | null {
    switch (rowState) {
        case ReconciliationRowStates.TemplateOnly:
            return comparator.fieldPolicy.allowAdd ? RegDataChangeTypes.AddNewFromTemplate : null;
        case ReconciliationRowStates.FlagDiff:
            if (fieldKey === WasteReturnRegFieldKeys.Ewc) return RegDataChangeTypes.SyncHazardousFromTemplate;
            if (fieldKey === WasteReturnRegFieldKeys.EaDr) return RegDataChangeTypes.SyncFlagsFromTemplate;
            return null;
        case ReconciliationRowStates.SystemOnly:
            return comparator.fieldPolicy.allowSoftDelete ? RegDataChangeTypes.SoftDeleteExisting : null;
        case ReconciliationRowStates.InactiveMatch:
            return RegDataChangeTypes.ReactivateExisting;
        case ReconciliationRowStates.DeletedMatch:
            return comparator.fieldPolicy.allowRestore ? RegDataChangeTypes.RestoreDeleted : null;
        default:
            return null;
    }
}

function resolveActionIcon(rowState: ReconciliationRowState, isStaged: boolean, stageChangeType: string | null): ReconciliationActionIcon {
    if (isStaged) {
        if (stageChangeType === RegDataChangeTypes.SoftDeleteExisting) {
            return ReconciliationActionIcons.StagedDelete;
        }
        return ReconciliationActionIcons.StagedSync;
    }
    if (rowState === ReconciliationRowStates.Match) return ReconciliationActionIcons.Match;
    if (rowState === ReconciliationRowStates.SystemOnly && stageChangeType === RegDataChangeTypes.SoftDeleteExisting) {
        return ReconciliationActionIcons.SoftDelete;
    }
    if (stageChangeType) return ReconciliationActionIcons.Stage;
    return ReconciliationActionIcons.None;
}

function resolveLifecycleNote(
    rowState: ReconciliationRowState,
    fieldKey: string,
    _comparator: FieldComparatorConfig
): string | undefined {
    if (fieldKey === WasteReturnRegFieldKeys.Geography && rowState === ReconciliationRowStates.TemplateOnly) {
        return GeographyLifecycleMessages.templateOnlyAdd;
    }
    return undefined;
}

function buildGridRow(
    fieldKey: string,
    normalizedKey: string,
    index: number,
    match: IRegDataComparisonMatchRow | null,
    systemOption: IRegSystemOption | null,
    comparator: FieldComparatorConfig,
    stagedChanges: IRegDataStagedChange[]
): ReconciliationGridRow {
    const hasTemplate = match != null;
    const templateParsed = match?.templateOption.parsed ?? {};
    const templateRawValue = match?.templateOption.rawValue ?? null;
    const systemRecordId = systemOption?.id ?? match?.systemRecordId ?? null;

    const rowState = resolveRowState(hasTemplate, systemOption, templateParsed, comparator);
    const stageChangeType = resolveStageChangeType(rowState, fieldKey, comparator);
    const stagedChange = getStagedChangeForGridRow(
        fieldKey,
        normalizedKey,
        templateRawValue,
        systemRecordId,
        stagedChanges
    );
    const isStaged = stagedChange != null;
    const actionIcon = resolveActionIcon(rowState, isStaged, stageChangeType);
    const lifecycleNote = resolveLifecycleNote(rowState, fieldKey, comparator);

    const actionDisabled = stageChangeType == null;
    const actionDisabledReason =
        lifecycleNote ??
        (stageChangeType == null && rowState === ReconciliationRowStates.TemplateOnly
            ? "Add is not available for this field."
            : undefined);

    const templateSide: TemplateSideData = {
        match,
        templateOption: match?.templateOption ?? null,
        templateRawValue,
    };

    const systemSide: SystemSideData = {
        option: systemOption,
        systemRecordId,
        displayValue: systemOption?.displayValue ?? match?.systemDisplayValue ?? null,
    };

    const hazardIndeterminate =
        fieldKey === WasteReturnRegFieldKeys.Ewc && comparator.context.ewcHazardDeterminate === false;

    return {
        rowId: `${fieldKey}-grid-${normalizedKey}-${index}`,
        fieldKey,
        normalizedKey,
        rowState,
        actionIcon,
        stageChangeType,
        actionDisabled,
        actionDisabledReason,
        lifecycleNote,
        warnings: match?.warnings ?? [],
        templateSide,
        systemSide,
        hazardIndeterminate,
        isStaged,
        stagedChange,
        stagedChangeKey: stagedChange ? getStagedChangeKey(stagedChange) : undefined,
    };
}

export function buildReconciliationGrid(
    card: IRegDataComparisonFieldCard,
    stagedChanges: IRegDataStagedChange[],
    comparator?: FieldComparatorConfig
): ReconciliationGridRow[] {
    const config = comparator ?? buildFieldComparatorConfig(card);
    const matches = card.matches ?? [];
    const systemOptions = card.systemOptions ?? [];

    const templateByKey = buildTemplateByKey(matches, card.fieldKey);
    const systemByKey = buildSystemByKey(systemOptions, card.fieldKey);

    const allKeys = [...new Set([...templateByKey.keys(), ...systemByKey.keys()])].sort((a, b) =>
        a.localeCompare(b)
    );

    return allKeys.map((normalizedKey, index) => {
        const match = templateByKey.get(normalizedKey) ?? null;
        const systemOption = systemByKey.get(normalizedKey) ?? null;
        return buildGridRow(card.fieldKey, normalizedKey, index, match, systemOption, config, stagedChanges);
    });
}

export function buildReconciliationCardModel(
    card: IRegDataComparisonFieldCard,
    stagedChanges: IRegDataStagedChange[]
): ReconciliationGridCardModel {
    const comparator = buildFieldComparatorConfig(card);
    const gridRows = buildReconciliationGrid(card, stagedChanges, comparator);

    return {
        fieldKey: card.fieldKey,
        label: card.label,
        summary: computeReconciliationCardSummary(gridRows),
        warnings: card.warnings ?? [],
        gridRows,
        hazardIndeterminate: comparator.context.ewcHazardDeterminate === false,
    };
}

export function buildReconciliationDisplayModels(
    comparison: { fields?: IRegDataComparisonFieldCard[] },
    stagedChanges: IRegDataStagedChange[]
): ReconciliationGridCardModel[] {
    return (comparison.fields ?? []).map((card) => buildReconciliationCardModel(card, stagedChanges));
}
